export interface GetClickAwayMouseEventArgs {
  hasPendingCreate: boolean
  autoApply: boolean
}

/**
 * Picks the event DropdownSelect's ClickAwayListener listens on.
 *
 * ClickAwayListener defaults to onClick, and React attaches its handlers at the root container —
 * so an outside submit button's onClick runs BEFORE the document-level click-away, and reads the
 * old value. Listening on mousedown makes the click-away commit first: mousedown is a discrete
 * event, so the parent re-renders with the committed value before the button's click fires.
 *
 * Two cases hold a value that only click-away commits:
 * - a pending create (typed search text, see `shouldCommitPendingCreate`)
 * - an `autoApply={false}` select, whose picks sit in `intermediateValue` until it closes
 *   (#24592: product Estimate Types picks were lost when the user clicked Done with the menu open)
 *
 * The autoApply={false} case must not depend on whether the menu is open. Switching from onClick
 * to onMouseDown in the click that opens the menu made the menu take two outside clicks to close
 * (seen in Chrome; jsdom does not reproduce it). The likely cause: the switch removes the document
 * click listener before that click reaches it, so ClickAwayListener's "inside" flag stays set and
 * the first outside mousedown is read as inside.
 *
 * Every other select keeps the onClick behaviour.
 */
export function getClickAwayMouseEvent({
  hasPendingCreate,
  autoApply
}: GetClickAwayMouseEventArgs): 'onMouseDown' | 'onClick' {
  return hasPendingCreate || !autoApply ? 'onMouseDown' : 'onClick'
}
